<!--
  IGNTR mobile bottom nav + "More" overflow sheet — visible <lg.
  Matches the handoff template at .claude/igntr-handoff/.../shared/sidebar.component.ts.

  Layout:
   - 5 primary tabs (Home / Sprints / Tools / Plan / Inbox) — fixed labels,
     feature-flag filtered (a tab is omitted if the tenant doesn't have the
     feature enabled).
   - "More" button on the right opens the inline bottom sheet that lists
     every nav target grouped by section (Main / Ignite / Admin Panel).
-->
<nav
  class="igntr-theme bottom-nav flex w-full items-end justify-around bg-white/95 px-1.5 pt-2 backdrop-blur border-t border-slate-200/70 pb-[max(env(safe-area-inset-bottom),8px)] font-sans select-none"
  role="navigation"
  aria-label="Primary"
>
  <a
    *ngFor="let tab of mobileTabs"
    [routerLink]="locked && !isHomeTab(tab) ? null : tab.path"
    (click)="locked && !isHomeTab(tab) ? $event.preventDefault() : null"
    [class.is-active]="checkUrl(tab.path)"
    [class.opacity-40]="locked && !isHomeTab(tab)"
    [class.cursor-not-allowed]="locked && !isHomeTab(tab)"
    [attr.aria-label]="tab.label"
    [attr.aria-current]="checkUrl(tab.path) ? 'page' : null"
    [pTooltip]="lockedTooltip"
    [tooltipDisabled]="!(locked && !isHomeTab(tab))"
    tooltipPosition="top"
    tooltipStyleClass="igntr-lock-tooltip"
    class="bottom-nav-btn relative flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-1 text-slate-500 no-underline"
  >
    <span
      *ngIf="checkUrl(tab.path)"
      class="absolute top-0 h-[2px] w-6 rounded-full bg-[var(--primary-color)]"
      aria-hidden="true"
    ></span>
    <span
      class="bottom-nav-icon relative inline-flex items-center justify-center"
    >
      <igntr-icon [name]="tab.icon" [size]="22" [stroke]="1.8"></igntr-icon>
      <ng-container *ngIf="tab.label === 'Inbox'">
        <span
          *ngIf="inboxBadge$ | async as count"
          class="absolute -right-1.5 -top-1 flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-[var(--app-theme-color)] px-1 text-[8.5px] font-semibold text-white"
        >
          {{ count > 9 ? "9+" : count }}
        </span>
      </ng-container>
    </span>
    <span
      class="bottom-nav-label truncate max-w-full text-[11px] font-medium leading-none"
    >
      {{ tab.displayName || tab.labelKey | transloco }}
    </span>
  </a>

  <!-- "More" — opens the inline overflow sheet (NOT the desktop sidebar
       drawer). Highlighted on routes outside the 5 primary tabs, or while
       the sheet itself is open. -->
  <button
    type="button"
    (click)="openMore()"
    [class.is-active]="isMoreActive()"
    class="bottom-nav-btn relative flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-1 text-slate-500"
    aria-label="More"
  >
    <span
      *ngIf="isMoreActive()"
      class="absolute top-0 h-[2px] w-6 rounded-full bg-[var(--primary-color)]"
      aria-hidden="true"
    ></span>
    <span
      class="bottom-nav-icon relative inline-flex items-center justify-center"
    >
      <igntr-icon name="dots" [size]="22" [stroke]="1.8"></igntr-icon>
    </span>
    <span class="bottom-nav-label text-[11px] font-medium leading-none">
      {{ "more" | transloco }}
    </span>
  </button>
</nav>

<!--
  Mobile overflow sheet — listed nav targets grouped by section. Mirrors the
  handoff template's `moreOpen` panel: drag handle, header with
  "IGNTR · {tenant}" tagline + page title + close button, then stacked
  sections (Main / Ignite [AI] / Admin Panel). Tap a row to navigate; the
  sheet closes itself.
-->
<ng-container *ngIf="moreOpen">
  <div
    class="igntr-theme more-sheet-backdrop"
    (click)="closeMore()"
    aria-hidden="true"
  ></div>
  <div
    class="igntr-theme more-sheet"
    role="dialog"
    aria-modal="true"
    aria-label="Menu"
  >
    <!-- Drag handle -->
    <div class="pt-2 pb-1 flex justify-center">
      <span class="w-9 h-1 rounded-full bg-slate-300"></span>
    </div>

    <!-- Header -->
    <header
      class="px-5 pt-1 pb-3 flex items-center justify-between border-b border-slate-200/70"
    >
      <div>
        <div
          class="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500"
        >
          IGNTR<span *ngIf="activeTenantLabel$ | async as tenant">
            · {{ tenant }}</span
          >
        </div>
        <div class="text-[15px] font-semibold text-slate-900 mt-0.5">
          {{ "navigate" | transloco }}
        </div>
      </div>
      <button
        type="button"
        (click)="closeMore()"
        class="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center"
        aria-label="Close"
      >
        <igntr-icon name="close" [size]="16"></igntr-icon>
      </button>
    </header>

    <!--
      Active tenant + switcher. Only renders when the user has access to
      >1 tenant. Mirrors the desktop sidebar's tenant chip but light-themed
      and with a native expandable list (no ng-select dependency on mobile).
    -->
    <section *ngIf="tenantsOptions.length > 1" class="px-3 pt-3 pb-1">
      <div
        class="px-2 pb-1.5 font-mono text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500"
      >
        {{ "activeTenant" | transloco }}
      </div>
      <button
        type="button"
        (click)="toggleTenantPicker()"
        class="tenant-tile flex w-full items-center gap-3 px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-left transition-colors hover:bg-slate-50"
        [attr.aria-expanded]="tenantPickerOpen"
      >
        <span
          class="flex h-9 w-9 items-center justify-center rounded-lg text-[13px] font-semibold text-white shrink-0"
          [attr.style]="'background: ' + activeTenantAvatarBg"
          aria-hidden="true"
        >
          {{ activeTenantInitial }}
        </span>
        <span
          class="flex-1 min-w-0 truncate text-[14px] font-semibold text-slate-900"
        >
          {{ activeTenantLabel || ("activeTenant" | transloco) }}
        </span>
        <span
          class="text-slate-400 transition-transform"
          [class.rotate-180]="tenantPickerOpen"
          aria-hidden="true"
        >
          <igntr-icon name="chevron" [size]="16"></igntr-icon>
        </span>
      </button>

      <!-- Tenant list — expanded below the tile when the user taps it -->
      <div
        *ngIf="tenantPickerOpen"
        class="mt-2 rounded-xl border border-slate-200 bg-white overflow-hidden"
        role="listbox"
      >
        <button
          *ngFor="let opt of tenantsOptions"
          type="button"
          (click)="setActiveTenant(opt.value)"
          [class.is-active-tenant]="opt.value === tenantId"
          class="tenant-row flex w-full items-center gap-3 px-3 py-2.5 text-left border-b border-slate-100 last:border-b-0 transition-colors active:bg-slate-100"
          role="option"
          [attr.aria-selected]="opt.value === tenantId"
        >
          <span
            class="flex h-8 w-8 items-center justify-center rounded-md text-[11px] font-semibold text-white shrink-0"
            [attr.style]="'background: ' + avatarBgFor(opt.label)"
            aria-hidden="true"
          >
            {{ (opt.label || "?").charAt(0).toUpperCase() }}
          </span>
          <span class="flex-1 truncate text-[13.5px] text-slate-800">
            {{ opt.label }}
          </span>
          <span
            *ngIf="opt.value === tenantId"
            class="text-[var(--primary-color)]"
            aria-hidden="true"
          >
            <igntr-icon name="check" [size]="16"></igntr-icon>
          </span>
        </button>
      </div>
    </section>

    <!-- Section list -->
    <ng-container *ngIf="sections$ | async as sections">
      <section *ngFor="let section of sections" class="px-3 pt-3 pb-1">
        <div class="px-2 pb-1.5 flex items-center gap-1.5">
          <span
            class="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500"
          >
            {{ section.label }}
          </span>
          <span
            *ngIf="section.badge"
            class="text-[9px] font-semibold px-1.5 py-0.5 rounded text-[var(--igntr-brand)] bg-[var(--igntr-brand)]/10"
          >
            {{ section.badge }}
          </span>
        </div>
        <div class="grid grid-cols-1">
          <ng-container *ngFor="let item of section.items">
            <a
              *ngIf="item.routerLink; else disabledRow"
              [routerLink]="
                locked && !isHomeRow(item) ? null : item.routerLink
              "
              (click)="
                locked && !isHomeRow(item)
                  ? $event.preventDefault()
                  : closeMore()
              "
              [class.is-row-active]="checkUrl(item.routerLink)"
              [class.opacity-40]="locked && !isHomeRow(item)"
              [class.cursor-not-allowed]="locked && !isHomeRow(item)"
              [pTooltip]="lockedTooltip"
              [tooltipDisabled]="!(locked && !isHomeRow(item))"
              tooltipPosition="top"
              tooltipStyleClass="igntr-lock-tooltip"
              class="more-sheet-row flex items-center gap-3 px-3 py-3 rounded-xl no-underline text-slate-800 transition-colors"
            >
              <span
                class="more-sheet-tile flex h-9 w-9 items-center justify-center rounded-lg shrink-0 bg-slate-100 text-slate-600"
              >
                <igntr-icon [name]="item.icon" [size]="16"></igntr-icon>
              </span>
              <span class="flex-1 text-[14px] font-medium">{{
                item.displayedName | transloco
              }}</span>
              <span
                *ngIf="item.notificationsCount"
                class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[var(--app-theme-color)] text-white"
                >{{
                  item.notificationsCount > 9 ? "9+" : item.notificationsCount
                }}</span
              >
              <span class="text-slate-400">
                <igntr-icon name="chevronR" [size]="16"></igntr-icon>
              </span>
            </a>
            <ng-template #disabledRow>
              <div
                class="flex items-center gap-3 px-3 py-3 rounded-xl text-slate-400 opacity-60"
              >
                <span
                  class="flex h-9 w-9 items-center justify-center rounded-lg shrink-0 bg-slate-100"
                >
                  <igntr-icon [name]="item.icon" [size]="16"></igntr-icon>
                </span>
                <span class="flex-1 text-[14px] font-medium">{{
                  item.displayedName | transloco
                }}</span>
                <span class="text-[10px] uppercase tracking-wider">
                  {{ "comingSoon" | transloco }}
                </span>
              </div>
            </ng-template>
          </ng-container>
        </div>
      </section>
    </ng-container>

    <footer
      class="px-3 flex items-center justify-between border-t border-slate-200/70"
    >
      <app-menuuser-card [isMobile]="true"> </app-menuuser-card>
    </footer>
  </div>
</ng-container>
